import React, { useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UploadCloud, X, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { uploadChunked } from '@/lib/chunkedUpload';
import { ACCEPT, ALLOWED_EXT, MAX_SIZE, CATEGORY_CONFIG, formatSize } from '@/components/library/libraryMeta';

const checkFile = (file) => {
  const ext = file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : '';
  if (!ALLOWED_EXT.includes(ext)) return `.${ext || '?'} files are not allowed`;
  if (file.size > MAX_SIZE) return 'Larger than 100MB';
  if (!file.size) return 'File is empty';
  return '';
};

const FileRow = ({ entry, onRemove, busy }) => (
  <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2" data-testid={`upload-row-${entry.key}`}>
    <div className="flex items-center gap-2 text-xs">
      {entry.status === 'done' && <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />}
      {entry.status === 'error' && <AlertCircle size={14} className="text-red-400 shrink-0" />}
      {entry.status === 'uploading' && <Loader2 size={14} className="text-cyan-400 animate-spin shrink-0" />}
      <span className="flex-1 truncate text-[hsl(var(--text-primary))]" title={entry.file.name}>{entry.file.name}</span>
      <span className="text-[hsl(var(--text-muted))] shrink-0">{formatSize(entry.file.size)}</span>
      {!busy && entry.status !== 'done' && (
        <button onClick={() => onRemove(entry.key)} className="text-[hsl(var(--text-muted))] hover:text-red-400" data-testid={`upload-remove-${entry.key}`}><X size={13} /></button>
      )}
    </div>
    {entry.error && <p className="text-[11px] text-red-400 mt-1" data-testid={`upload-error-${entry.key}`}>{entry.error}</p>}
    {(entry.status === 'uploading' || entry.status === 'done') && (
      <div className="h-1 mt-2 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-600 transition-[width] duration-200" style={{ width: `${Math.round(entry.progress * 100)}%` }}
          data-testid={`upload-progress-${entry.key}`} />
      </div>
    )}
  </div>
);

export const UploadDialog = ({ open, onOpenChange, folderId, folderLabel, onUploaded }) => {
  const [entries, setEntries] = useState([]);
  const [category, setCategory] = useState('general');
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const patch = (key, p) => setEntries((list) => list.map((e) => (e.key === key ? { ...e, ...p } : e)));
  const addFiles = (files) => setEntries((list) => [...list, ...Array.from(files).map((file) => {
    const error = checkFile(file);
    return { key: `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 7)}`, file, progress: 0, status: error ? 'error' : 'queued', error };
  })]);
  const close = (v) => { if (busy) return; if (!v) { setEntries([]); setCategory('general'); } onOpenChange(v); };

  const start = async () => {
    setBusy(true);
    let ok = 0;
    for (const entry of entries.filter((e) => e.status === 'queued')) {
      patch(entry.key, { status: 'uploading' });
      try {
        const item = await uploadChunked(entry.file, { folder_id: folderId || null, category }, (p) => patch(entry.key, { progress: p }));
        patch(entry.key, { status: 'done', progress: 1 });
        onUploaded(item);
        ok += 1;
      } catch (err) {
        patch(entry.key, { status: 'error', error: err.response?.data?.detail || 'Upload failed — please retry' });
      }
    }
    setBusy(false);
    if (ok) toast.success(`${ok} file${ok > 1 ? 's' : ''} uploaded`);
  };

  const queued = entries.filter((e) => e.status === 'queued').length;
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-lg" data-testid="upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UploadCloud size={18} className="text-cyan-400" /> Upload files</DialogTitle>
          <DialogDescription>To: Library{folderLabel ? ` / ${folderLabel}` : ''} · PDF, Word, PowerPoint, Excel, images, video · max 100MB each</DialogDescription>
        </DialogHeader>
        <div onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); if (!busy) addFiles(e.dataTransfer.files); }}
          className={`rounded-xl border-2 border-dashed px-4 py-8 text-center cursor-pointer transition-colors ${dragging ? 'border-cyan-400 bg-cyan-500/10' : 'border-[hsl(var(--border))] hover:border-cyan-500/50'}`}
          data-testid="upload-dropzone">
          <UploadCloud size={28} className="mx-auto text-cyan-400/70 mb-2" />
          <p className="text-sm text-[hsl(var(--text-primary))]">Drop files here or <span className="text-cyan-400 font-medium">browse</span></p>
          <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden" disabled={busy}
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-testid="upload-file-input" />
        </div>
        {!!entries.length && (
          <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1" data-testid="upload-list">
            {entries.map((e) => <FileRow key={e.key} entry={e} busy={busy} onRemove={(k) => setEntries((l) => l.filter((x) => x.key !== k))} />)}
          </div>
        )}
        <div>
          <label className="text-xs text-[hsl(var(--text-muted))] mb-1 block">Category</label>
          <Select value={category} onValueChange={setCategory} disabled={busy}>
            <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]" data-testid="upload-category"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(CATEGORY_CONFIG).map(([k, c]) => <SelectItem key={k} value={k}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} disabled={busy} data-testid="upload-cancel-btn">{queued ? 'Cancel' : 'Close'}</Button>
          <Button onClick={start} disabled={busy || !queued} className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white" data-testid="upload-start-btn">
            {busy ? <Loader2 size={15} className="animate-spin mr-2" /> : <UploadCloud size={15} className="mr-2" />}
            {busy ? 'Uploading…' : `Upload${queued ? ` ${queued}` : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
